/**
 * CustomerMessageRenderer
 *
 * Renders a single message in the customer chat view using DS timeline components.
 * Uses AgentResponse/UserMessage for visual consistency with admin-ui.
 *
 * Handles message types:
 * - agent_turn: user prompt + assistant response (DS UserMessage + AgentResponse)
 * - tool_call: compact summary (DS SystemMessage)
 * - tool_result: the file cards of the files it made (OutputFileBlock);
 *   without files, a compact summary (DS SystemMessage)
 * - hitl_request: tell_user (message modality) or ask_user (interactive)
 * - hitl_response: user response (DS UserMessage), with its delivery line
 *   when this chat sent it (DeliveryStatus)
 * - orchestrator_decision: manager reasoning (DS SystemMessage)
 * - delegation: agent started working (DS AgentActivity)
 * - delegation_result: agent completed/failed/blocked by setup (DS AgentActivity)
 * - agent_thinking: animated thinking indicator (DS ThinkingBlock)
 * - governance_event: policy enforcement (DS SystemMessage)
 * - transcript: voice speech (user/agent) as chat bubbles, interim shown in italic
 * - mission_trigger: MissionTriggerCard
 * - operator_turn: a person's reply in an operator takeover, under their
 *   display name with a Person marker (DS UserMessage), never the agent's
 *
 * The new chat draws the file box, the delivery line and the mission trigger
 * through the chat surface seam (OutputFileGroup, DeliveryStatus,
 * MissionTrigger); without a provider each is the current component.
 * - mission_completed / mission_failed: null (handled by parent)
 * - node_status: null (Activity sidebar only)
 *
 * The switch covers every ChatMessageKind and ends in unknownKind()
 * (@agion/mission-stream), so a kind added without a case fails tsc.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  AgentResponse,
  AgentActivity,
  blockedReasonText,
  resolveDelegationState,
  UserMessage,
  SystemMessage,
  ThinkingBlock,
  EscalationCard,
  FileAttachment,
  type FileAttachmentFile,
  type EscalationSeverity,
  type AgentResponseGovernance,
} from '@agion/ui'
import { toolResultFiles, unknownKind, type QuestionReply } from '@agion/mission-stream'
import { MarkdownContent } from './MarkdownContent'
import { MissionTriggerCard } from './MissionTriggerCard'
import { HITLPrompt } from './HITLPrompt'
import { QuestionCard } from './QuestionCard'
import { APPROVE_ANSWER, DENY_ANSWER, DecideInEscalations, approvalEscalationId, approvalMode, escalationsHref } from './governanceApproval'
import type { HITLDecision } from '../../types/streaming'
import { OutputFileBlock } from './OutputFileBlock'
import { DeliveryStatus } from './DeliveryStatus'
import { questionClosedText } from './deliveryTexts'
import {
  useChatSurface,
  type AgentResponseProps,
  type DeliveryStatusProps,
  type EscalationProps,
  type FileCardProps,
  type MarkdownProps,
  type MissionTriggerProps,
  type OutputFileGroupProps,
  type OutputFileProps,
  type QuestionCardProps,
  type QuestionProps,
  type SystemMessageProps,
  type ThinkingProps,
  type ToolActivityProps,
  type UserMessageProps,
} from './chatSurface'
import type { StreamingChatMessage, ActiveHITLRequest, QuestionClosedNote } from '../../hooks/useStreamingMissionChat'
import { questionCountdown, questionOptionDetails, type ChoiceQuestion } from '../../utils/questionOptions'
import type { QuestionCardRef } from '../../utils/questionCard'
import type { AnswerOrigin, MessageDelivery } from '../../hooks/useAnswerDelivery'
import type { HeldSubject } from '../../types/escalations'
import { useBlockedOutcomeLabels } from '../../hooks/useBlockedOutcomeLabels'

/** Format time as HH:MM */
export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * Legacy ChatBubble — kept for backward compatibility with tests.
 * New code should use AgentResponse/UserMessage directly.
 */
export const ChatBubble: React.FC<{
  sender: 'user' | 'agent'
  label?: string
  time?: string
  children: React.ReactNode
}> = ({ sender, label, children }) => {
  if (sender === 'user') {
    return (
      <UserMessage userName={label || 'You'} isLast>
        {children}
      </UserMessage>
    )
  }
  return (
    <AgentResponse agentName={label || 'Agent'} isLast showCopy>
      {children}
    </AgentResponse>
  )
}

/** Parsed file attachment from the mission-runner annotation */
interface ParsedAttachment {
  fileId: string
  filename: string
  contentType: string
}

/** Result of extracting user message from enriched prompt */
interface ExtractedMessage {
  text: string | null
  attachments: ParsedAttachment[]
}

/**
 * Extract the user's actual message from a potentially enriched prompt.
 * Strips system-injected sections (## Context, ## Constraints, etc.),
 * parses [Attached files] annotations into structured data,
 * and returns null for tool-result continuation turns.
 */
function extractUserMessage(prompt: string): ExtractedMessage {
  if (prompt.startsWith('[Tool results from previous turn]')) return { text: null, attachments: [] }

  // Strip [Attached files] block and DELEGATION REQUIRED directive
  const attachments: ParsedAttachment[] = []
  const attachedFilesIdx = prompt.indexOf('\n\n[Attached files]')
  let cleanedPrompt = prompt
  if (attachedFilesIdx !== -1) {
    // Parse file entries: - file_id="..." filename="..." type="..."
    const attachBlock = prompt.slice(attachedFilesIdx)
    const filePattern = /file_id="([^"]+)"\s+filename="([^"]+)"\s+type="([^"]+)"/g
    let m: RegExpExecArray | null
    while ((m = filePattern.exec(attachBlock)) !== null) {
      attachments.push({ fileId: m[1]!, filename: m[2]!, contentType: m[3]! })
    }
    cleanedPrompt = prompt.slice(0, attachedFilesIdx).trim()
  }

  const sectionPattern = /^##\s+(Context|Constraints|Available\s+Tools|Instructions|Task|Rules)/m
  const match = cleanedPrompt.search(sectionPattern)
  if (match > 0) {
    const userPart = cleanedPrompt.slice(0, match).trim()
    return { text: userPart || null, attachments }
  }
  return { text: cleanedPrompt || null, attachments }
}

// ============================================================================
// Main Renderer
// ============================================================================

export interface CustomerMessageRendererProps {
  message: StreamingChatMessage
  activeHITL: ActiveHITLRequest | null
  /** All pending HITL requests — used to show status indicators on HITL messages in the feed */
  pendingHITLs?: ActiveHITLRequest[]
  toolResults: Map<string, unknown>
  /** decision: only from an approval card decided inline (agion/Agion#2618). */
  onRespondToHITL: (requestId: string, response: string, attachments?: Array<{ host_file_id: string; filename: string; content_type: string; size: number }>, decision?: HITLDecision, answer?: QuestionReply) => void
  onOpenMission: (missionId: string) => void
  onFeedback?: (messageId: string, agentId: string | undefined, type: 'positive' | 'negative') => void
  /** Map from agent_id/agent_name to display name (from mission.agentNameMap) */
  agentNameMap?: Record<string, string>
  /** Agent ID of the mission orchestrator (shows as "Manager") */
  orchestratorAgentId?: string
  /** Current authenticated user ID — used to show "You" vs actual name for multi-user chats */
  currentUserId?: string
  /** Map from user_id → display name (from mission.participantNames) */
  participantNames?: Record<string, string>
  /** Hide the user message header (avatar + name) for grouped consecutive messages */
  hideUserHeader?: boolean
  /** Hide the agent message header (avatar + name) for grouped consecutive messages */
  hideAgentHeader?: boolean
  /** BYOH host executing the mission — enables tool output file downloads */
  hostId?: string
  /** The run has ended — a question left unanswered shows no status label */
  runEnded?: boolean
  /** The run ended without converging (failed or stopped). Reopened from history the
   *  pending list is empty, so an unanswered question would otherwise read "Answered". */
  runUnsuccessful?: boolean
  /** No member's words follow this message in the conversation. */
  afterLastAnswer?: boolean
  /** Operator mode (agion/Agion#3435): the held run's end user, named only by the hold read. */
  heldSubject?: HeldSubject
}

// PLAN-20260929 slice 7: the turn and notice chrome come from the chat surface
// when it provides them, otherwise the current chat's own component. Each is
// resolved only when that branch renders.
function SurfaceUserMessage(props: UserMessageProps) {
  const SurfaceComponent = useChatSurface().UserMessage
  if (SurfaceComponent) return <SurfaceComponent {...props} />
  // The current chat keeps its own styling: it colours a message by
  // isCurrentUser, which it has never been given here.
  const { isCurrentUser: _isCurrentUser, ...current } = props
  return <UserMessage {...current} />
}
function SurfaceAgentResponse(props: AgentResponseProps) {
  const Component = useChatSurface().AgentResponse ?? AgentResponse
  return <Component {...props} />
}
function SurfaceSystemMessage(props: SystemMessageProps) {
  const Component = useChatSurface().SystemMessage ?? SystemMessage
  return <Component {...props} />
}
function SurfaceQuestion(props: QuestionProps) {
  const Component = useChatSurface().Question ?? HITLPrompt
  return <Component {...props} />
}
function SurfaceFileCard(props: FileCardProps) {
  const Component = useChatSurface().FileCard ?? (({ files }: FileCardProps) => <FileAttachment compact files={files} />)
  return <Component {...props} />
}
// A delegation and a thinking turn are Activity-panel kinds (messagePlacement,
// @agion/mission-stream), so no surface overrides them; the new chat shows the
// run's steps instead (agion/Agion#2827).
function ToolActivityRow({ agentName, status, activity, result, duration, blockedLabel }: ToolActivityProps) {
  return <AgentActivity agentName={agentName} status={status} activity={activity} result={result} duration={duration} blockedLabel={blockedLabel} isLast />
}
function ThinkingRow({ agentName, currentStep }: ThinkingProps) {
  return <ThinkingBlock agentName={agentName} currentStep={currentStep} isLast />
}
function SurfaceOutputFile(props: OutputFileProps) {
  const Component = useChatSurface().OutputFile ?? (({ file, hostId }: OutputFileProps) => <OutputFileBlock file={file} hostId={hostId} />)
  return <Component {...props} />
}
function SurfaceOutputFileGroup(props: OutputFileGroupProps) {
  const Component = useChatSurface().OutputFileGroup ?? (({ children }: OutputFileGroupProps) => (
    <div className="pl-11 space-y-1.5" data-testid="tool-result-files">
      {children}
    </div>
  ))
  return <Component {...props} />
}
function SurfaceMissionTrigger(props: MissionTriggerProps) {
  const Component = useChatSurface().MissionTrigger ?? (({ childMissionId, childMissionName, childExecutionId, onOpenMission }: MissionTriggerProps) => (
    <div className="pl-11">
      <MissionTriggerCard
        childMissionId={childMissionId}
        childMissionName={childMissionName}
        childExecutionId={childExecutionId}
        onOpenMission={(missionId) => onOpenMission(missionId)}
      />
    </div>
  ))
  return <Component {...props} />
}
function SurfaceDeliveryStatus(props: DeliveryStatusProps) {
  const Component = useChatSurface().DeliveryStatus ?? DeliveryStatus
  return <Component {...props} />
}
function SurfaceMarkdown(props: MarkdownProps) {
  const Component = useChatSurface().Markdown ?? MarkdownContent
  return <Component {...props} />
}
function SurfaceQuestionCard(props: QuestionCardProps) {
  const Component = useChatSurface().QuestionCard ?? QuestionCard
  return <Component {...props} />
}

function SurfaceEscalation(props: EscalationProps) {
  const Component = useChatSurface().Escalation ?? (({ status, agentName, action, ruleName, severity, expiresAt, onApprove, onDeny, isReadOnly, decideInEscalationsHref }: EscalationProps) => (
    <>
      <EscalationCard
        type="action_approval"
        status={status}
        agentName={agentName}
        action={action}
        ruleName={ruleName}
        severity={severity}
        expiresAt={expiresAt}
        onApprove={onApprove}
        onDeny={onDeny}
        isReadOnly={isReadOnly || Boolean(decideInEscalationsHref)}
      />
      {status === 'pending' && !isReadOnly && decideInEscalationsHref && (
        <DecideInEscalations href={decideInEscalationsHref} />
      )}
    </>
  ))
  return <Component {...props} />
}

export const CustomerMessageRenderer: React.FC<CustomerMessageRendererProps> = ({
  message: msg, activeHITL, pendingHITLs = [], toolResults: _toolResults, onRespondToHITL, onOpenMission, onFeedback,
  agentNameMap, orchestratorAgentId, currentUserId, participantNames, hideUserHeader, hideAgentHeader, hostId,
  runEnded = false, runUnsuccessful = false, afterLastAnswer = false, heldSubject,
}) => {
  const { t } = useTranslation(['chat', 'common'])
  const blockedLabels = useBlockedOutcomeLabels()
  // A told message's text and feedback, when the surface opts in (F-CHAT-05, F-CHAT-07).
  const { toldMessageFeedback, toldMessageText } = useChatSurface()

  /** Resolve agent display name from agentId, nodeId, or orchestrator fallback */
  const resolveAgentName = (agentId?: string, nodeId?: string): string => {
    const nilUUID = '00000000-0000-0000-0000-000000000000'
    const effectiveAgentId = agentId && agentId !== nilUUID ? agentId : undefined

    // Try direct lookup by agent ID or node ID
    if (effectiveAgentId && agentNameMap?.[effectiveAgentId]) return agentNameMap[effectiveAgentId]
    if (nodeId && agentNameMap?.[nodeId]) return agentNameMap[nodeId]

    // Check if this is explicitly the orchestrator
    if (orchestratorAgentId && effectiveAgentId === orchestratorAgentId) {
      return agentNameMap?.[orchestratorAgentId] ?? t('common:orchestrator', 'Orchestrator')
    }

    // For adaptive missions, user-facing messages come from the orchestrator.
    // When agent ID is nil (common for all history messages), use orchestrator name.
    if (orchestratorAgentId && agentNameMap?.[orchestratorAgentId]) {
      return agentNameMap[orchestratorAgentId]
    }

    // Last resort: find any orchestrator-type node label in the map
    if (agentNameMap) {
      const orchLabel = agentNameMap['task-1'] // orchestrator node convention
      if (orchLabel) return orchLabel
    }

    return t('common:agent', 'Agent')
  }

  /**
   * Whether a user message is the current member's own: theirs by id, or one
   * this chat created itself (sentByThisChat) before its echo names the author.
   * A message without either is never the member's (agion/Agion#2039).
   */
  const isOwnMessage = (userId?: string, sentByThisChat?: boolean): boolean =>
    userId ? !!currentUserId && userId === currentUserId : sentByThisChat === true

  /**
   * The byline of a user message (agion/Agion#2039). Only the member's own
   * message carries the member's name. Anyone else's comes from the message or
   * from participantNames by the message's id; a message without an author
   * says its name was not recorded and is never given the viewer's name. In
   * operator mode the held run's end user is named only by heldSubject.
   */
  const resolveUserName = (userId?: string, userName?: string, sentByThisChat?: boolean): string => {
    if (isOwnMessage(userId, sentByThisChat)) {
      return (currentUserId && participantNames?.[currentUserId]) || userName || t('common:you', 'You')
    }
    if (heldSubject && userId === heldSubject.userId) {
      return heldSubject.displayName || t('common:unnamedPerson', 'Name not recorded')
    }
    if (userName) return userName
    if (userId && participantNames?.[userId]) return participantNames[userId]
    return t('common:unnamedPerson', 'Name not recorded')
  }

  switch (msg.type) {
    case 'agent_turn': {
      const d = msg.data as {
        userPrompt?: string
        assistantResponse?: string
        userId?: string
        governance?: { action: string; reason?: string; trustTier?: string }
      }
      const elements: React.ReactNode[] = []
      const extracted = d.userPrompt ? extractUserMessage(d.userPrompt) : { text: null, attachments: [] }

      // An agent turn's prompt carries no author of its own.
      const userName = resolveUserName(d.userId)

      if (extracted.text || extracted.attachments.length > 0) {
        elements.push(
          <SurfaceUserMessage
            key={`${msg.id}-user`}
            userName={userName}
            isCurrentUser={isOwnMessage(d.userId)}
            isLast
            showHeader={!hideUserHeader}
          >
            {extracted.attachments.length > 0 && (
              <div className="mb-1">
                <SurfaceFileCard
                  files={extracted.attachments.map((att, i): FileAttachmentFile => ({
                    id: `att-${msg.id}-${i}`,
                    name: att.filename,
                    size: 0,
                    type: att.contentType,
                    status: 'complete',
                  }))}
                />
              </div>
            )}
            {extracted.text && (
              <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{extracted.text}</p>
            )}
          </SurfaceUserMessage>
        )
      }

      // Build governance prop for AgentResponse shield icon
      const governance: AgentResponseGovernance | undefined = d.governance
        ? { action: d.governance.action as AgentResponseGovernance['action'], reason: d.governance.reason, trustTier: d.governance.trustTier }
        : undefined

      if (d.assistantResponse) {
        elements.push(
          <SurfaceAgentResponse
            key={`${msg.id}-agent`}
            agentName={resolveAgentName(msg.agentId, msg.nodeId)}
            isLast
            showCopy
            copyContent={d.assistantResponse}
            showFeedback
            onFeedback={(type) => onFeedback?.(msg.id, msg.agentId, type)}
            governance={governance}
            showHeader={!hideAgentHeader}
          >
            <SurfaceMarkdown content={d.assistantResponse} />
          </SurfaceAgentResponse>
        )
      }

      return <>{elements}</>
    }

    case 'tool_call': {
      const d = msg.data as { toolCallId: string; toolName: string; toolInput: Record<string, unknown> }
      return (
        <SurfaceSystemMessage variant="info" isLast>
          {resolveAgentName(msg.agentId, msg.nodeId)} {t('chat:tool.used', 'used')} {d.toolName}
        </SurfaceSystemMessage>
      )
    }

    case 'tool_result': {
      // A tool result that made files shows only their file cards: the files
      // are what the user gets; the raw tool output stays in the Activity sidebar.
      const files = toolResultFiles(msg.data)
      if (files.length > 0) {
        return (
          <SurfaceOutputFileGroup>
            {files.map((file) => (
              <SurfaceOutputFile key={file.fileId} file={file} hostId={hostId} />
            ))}
          </SurfaceOutputFileGroup>
        )
      }
      const d = msg.data as { toolName?: string; success?: boolean; output?: unknown; error?: string; durationMs?: number }
      const output = d.output as Record<string, unknown> | string | undefined
      // Extract human-readable summary
      let summary = ''
      let hits: Array<{ title?: string; snippet?: string; url?: string }> = []
      if (d.error) {
        summary = d.error
      } else if (output && typeof output === 'object') {
        const obj = output as Record<string, unknown>
        const errorObj = obj.error as Record<string, unknown> | string | undefined
        if (errorObj) {
          summary = typeof errorObj === 'string' ? errorObj : (errorObj as Record<string, unknown>).error_message as string ?? 'Error'
        } else {
          const query = (obj.query as string)
            ?? ((obj.provider_response as Record<string, unknown>)?.query as string)
            ?? ((obj.provider_raw_response as Record<string, unknown>)?.query as string)
          const provider = ((obj.provider_response as Record<string, unknown>)?.provider as string)
            ?? ((obj.provider_raw_response as Record<string, unknown>)?.provider as string)
            ?? (obj.provider as string)
          const message = obj.message as string | undefined
          const rawHits = (obj.hits ?? obj.results ?? (obj.provider_raw_response as Record<string, unknown>)?.results) as Array<{ title?: string; snippet?: string; url?: string }> | undefined
          hits = Array.isArray(rawHits) ? rawHits.slice(0, 3) : []
          if (query) summary = `Searched "${query}"${provider ? ` via ${provider}` : ''}`
          if (message) summary += summary ? ` — ${message}` : message
          if (!summary && typeof obj.result === 'string') summary = obj.result
          if (!summary && typeof obj.content === 'string') summary = obj.content
        }
      } else if (typeof output === 'string') {
        summary = output
      }
      if (!summary) summary = d.toolName ? `${d.toolName} completed` : t('chat:tool.completed', 'Tool completed')

      const isError = !d.success || !!d.error || !!(output && typeof output === 'object' && 'error' in (output as Record<string, unknown>))

      return (
        <SurfaceSystemMessage variant={isError ? 'error' : 'info'} isLast>
          <div className="text-xs space-y-1.5">
            <span>{d.toolName ? `${d.toolName}: ` : ''}{summary}</span>
            {hits.length > 0 && (
              <div className="space-y-1">
                {hits.map((hit, idx) => (
                  <div key={idx} className="flex items-start gap-1.5 ml-2">
                    <span className="text-slate-400 mt-0.5">&#8226;</span>
                    <span>
                      {hit.title && <span className="font-medium">{hit.title}</span>}
                      {hit.url && (
                        <a href={hit.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-1" onClick={(e) => e.stopPropagation()}>&#8599;</a>
                      )}
                      {hit.snippet && <span className="text-slate-500 dark:text-slate-400 ml-1">{hit.snippet.slice(0, 80)}{hit.snippet.length > 80 ? '...' : ''}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SurfaceSystemMessage>
      )
    }

    case 'hitl_request': {
      const d = msg.data as {
        requestId: string
        prompt: string
        options?: string[]
        /** The server's label and description per option (F-CHAT-06). */
        optionDetails?: unknown
        modality: string
        governanceContext?: Record<string, string>
        governance_context?: Record<string, string>
        /** The structured choice question, live or stored (agion/Agion#3559). */
        question?: ChoiceQuestion
        /** Set when the host closed the question (agion/Agion#2767). */
        questionClosed?: QuestionClosedNote
        /** A question card frame's escalation (agion/Agion#3446). */
        questionCard?: QuestionCardRef
      }
      // A question card frame names a question escalation only (agion/Agion#3446):
      // the card reads it from governance. Its prompt never shows, and it is
      // never answered as a question here.
      const questionCard = d.questionCard
      if (questionCard) {
        return (
          <div className="pl-11" data-testid="question-card-row">
            <SurfaceQuestionCard escalationId={questionCard.escalationId} />
          </div>
        )
      }
      const isActive = activeHITL?.requestId === d.requestId
      // A question the host closed says so under its card, closed or expired.
      const closedNote = d.questionClosed && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic" data-testid="question-closed" role="status">
          {questionClosedText(t, d.questionClosed.reason)}
        </p>
      )
      const pendingRequest = pendingHITLs.find((h) => h.requestId === d.requestId)
      const isPending = pendingRequest !== undefined

      // Check for governance context (handle both camelCase and snake_case)
      const govCtx = d.governanceContext || d.governance_context
      const hasGovernanceContext = govCtx && (govCtx.ruleName || govCtx.rule_name)

      // tell_user — render as agent message
      const toldMessage = () => (
        <SurfaceAgentResponse
          agentName={resolveAgentName(msg.agentId, msg.nodeId)}
          isLast
          showHeader={!hideAgentHeader}
          {...(toldMessageFeedback
            ? { showFeedback: true, onFeedback: (type: 'positive' | 'negative') => onFeedback?.(msg.id, msg.agentId, type) }
            : {})}
        >
          <SurfaceMarkdown content={toldMessageText ? toldMessageText(d.prompt, d.requestId) : d.prompt} />
        </SurfaceAgentResponse>
      )
      if (d.modality === 'message') return toldMessage()

      // Governance escalation card (approval with governance context)
      if (hasGovernanceContext && d.modality === 'approval') {
        const ruleName = govCtx.ruleName || govCtx.rule_name || 'unknown-rule'
        const severity = (govCtx.severity || 'high') as EscalationSeverity

        const ends = activeHITL ? questionCountdown({ ...activeHITL, receivedAt: activeHITL.receivedAt ?? msg.timestamp }) : undefined
        const expiresAt = ends?.lastsMs ? new Date(ends.from + ends.lastsMs) : undefined
        // Approve and Deny send the explicit decision, and only for a card
        // its runner decides inline; any other card links to Escalations
        // (agion/Agion#2618).
        const inline = approvalMode(govCtx) === 'inline'
        const approve = APPROVE_ANSWER
        const deny = DENY_ANSWER

        return (
          <div className="pl-11" data-testid="governance-escalation-card">
            <SurfaceEscalation
              status={isActive ? 'pending' : 'expired'}
              agentName={resolveAgentName(msg.agentId, msg.nodeId)}
              action={d.prompt}
              ruleName={ruleName}
              severity={severity}
              expiresAt={expiresAt}
              onApprove={() => { if (inline) onRespondToHITL(d.requestId, approve.option, undefined, approve.decision) }}
              onDeny={() => { if (inline) onRespondToHITL(d.requestId, deny.option, undefined, deny.decision) }}
              isReadOnly={!isActive}
              decideInEscalationsHref={inline ? undefined : escalationsHref(approvalEscalationId(govCtx))}
            />
            {closedNote}
          </div>
        )
      }

      // Option-based HITL — render question text + option buttons inline
      if (d.options && d.options.length > 0) {
        const hitlRequest: ActiveHITLRequest = {
          requestId: d.requestId,
          nodeId: '',
          agentId: '',
          prompt: d.prompt,
          options: d.options,
          ...(questionOptionDetails(d.optionDetails) ? { optionDetails: questionOptionDetails(d.optionDetails) } : {}),
          ...(d.question ? { question: d.question } : {}),
          modality: d.modality,
          receivedAt: msg.timestamp,
          ...(isActive && activeHITL ? {
            executionId: activeHITL.executionId,
            timeoutMs: activeHITL.timeoutMs,
            governanceContext: activeHITL.governanceContext,
          } : {}),
          // Every open governance card keeps its marks, selected or not, so an
          // approval is never offered as plain options (agion/Agion#2618).
          ...(pendingRequest?.governanceContext ? { governanceContext: pendingRequest.governanceContext } : {}),
        }
        return (
          <div id={`hitl-req-${d.requestId}`}>
            <SurfaceQuestion
              request={hitlRequest}
              onRespond={(requestId, response, decision, answer) => {
                if (answer) onRespondToHITL(requestId, response, undefined, undefined, answer)
                else if (decision) onRespondToHITL(requestId, response, undefined, decision)
                else onRespondToHITL(requestId, response)
              }}
              isOpen={isPending && !runEnded}
            />
            {closedNote}
          </div>
        )
      }

      // A run's stored record keeps what it told the member as a plain assistant
      // row without its modality (mission-runner pkg/chat/reporter.go), so a run
      // read back from history brings a step's shared outputs here as free text.
      // One the member never answered (no member's words follow it) in a run
      // that completed is what the run told them: a surface that gives told
      // messages feedback draws it as one, without "Answered" (F-CHAT-07).
      if (toldMessageFeedback && afterLastAnswer && runEnded && !runUnsuccessful && !isPending && !closedNote) {
        return toldMessage()
      }

      // Free-text HITL — render as agent message only (input is via chat input + context bar)
      return (
        <div id={`hitl-req-${d.requestId}`}>
          <SurfaceAgentResponse agentName={resolveAgentName(msg.agentId, msg.nodeId)} isLast showHeader={!hideAgentHeader}>
            <SurfaceMarkdown content={d.prompt} />
            {/* Status indicator. A question still unanswered when the run ended is
                neither awaiting an answer nor answered, so it carries no label.
                In a run that ended without converging the pending list is empty once
                it is reopened from history, so "Answered" would be a false claim. So
                too while the run goes on: a question no member's words follow left
                the pending list unanswered. */}
            {isPending ? (runEnded ? null : (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">
                {t('chat:hitl.awaitingResponse', 'Awaiting your response...')}
              </p>
            )) : closedNote ? closedNote : runUnsuccessful || (afterLastAnswer && !runEnded) ? null : (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 italic">
                {t('chat:hitl.answered', 'Answered')}
              </p>
            )}
          </SurfaceAgentResponse>
        </div>
      )
    }

    case 'hitl_response': {
      const d = msg.data as {
        response?: string
        requestId?: string
        userId?: string
        userName?: string
        attachments?: Array<{ filename: string; contentType: string; size: number }>
        /** Set on an answer this chat sent (hooks/useAnswerDelivery.ts, phase 4). */
        delivery?: MessageDelivery
        deliveryOrigin?: AnswerOrigin
        /** Set on a message this chat created itself (agion/Agion#2039). */
        sentByThisChat?: boolean
      }
      if (!d.response && !d.attachments?.length) return null
      const displayName = resolveUserName(d.userId, d.userName, d.sentByThisChat)
      return (
        <SurfaceUserMessage
          userName={displayName}
          isCurrentUser={isOwnMessage(d.userId, d.sentByThisChat)}
          isLast
          showHeader={!hideUserHeader}
        >
          {d.attachments && d.attachments.length > 0 && (
            <div className="mb-1">
              <SurfaceFileCard
                files={d.attachments.map((att, i): FileAttachmentFile => ({
                  id: `att-${msg.id}-${i}`,
                  name: att.filename,
                  size: att.size,
                  type: att.contentType,
                  status: 'complete',
                }))}
              />
            </div>
          )}
          {d.response && (
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{d.response}</p>
          )}
          {d.delivery && <SurfaceDeliveryStatus delivery={d.delivery} origin={d.deliveryOrigin} />}
        </SurfaceUserMessage>
      )
    }

    case 'mission_trigger': {
      const d = msg.data as {
        childMissionId: string
        childMissionName?: string
        childExecutionId: string
      }
      return (
        <SurfaceMissionTrigger
          childMissionId={d.childMissionId}
          childMissionName={d.childMissionName}
          childExecutionId={d.childExecutionId}
          onOpenMission={onOpenMission}
        />
      )
    }

    case 'governance_event': {
      const d = msg.data as {
        decision?: string
        action?: string
        reason?: string
        agentName?: string
        ruleName?: string
        trustTier?: string
      }
      // Only show non-allowed governance decisions to avoid noise
      if (d.decision === 'allowed') return null
      const isBlocked = d.decision === 'blocked' || d.decision === 'denied'
      const isObfuscated = d.decision === 'obfuscated'
      return (
        <SurfaceSystemMessage variant={isBlocked ? 'warning' : isObfuscated ? 'warning' : 'info'} isLast>
          {isObfuscated
            ? t('chat:governance.contentMasked', 'Content masked by policy: {{rule}}', { rule: d.ruleName || d.action || 'unknown' })
            : d.action || d.reason || t('chat:governance.policyDecision', 'Policy decision applied')}
          {d.agentName && <span className="ml-1 text-slate-400">({d.agentName})</span>}
        </SurfaceSystemMessage>
      )
    }

    case 'orchestrator_decision': {
      const d = msg.data as {
        reasoning?: string
        action?: string
        targetAgent?: string
      }
      const snippet = d.reasoning
        ? d.reasoning.length > 120 ? d.reasoning.slice(0, 120) + '...' : d.reasoning
        : ''
      const targetLabel = d.targetAgent
        ? agentNameMap?.[d.targetAgent] || d.targetAgent
        : undefined
      return (
        <SurfaceSystemMessage variant="info" isLast>
          {resolveAgentName(msg.agentId, msg.nodeId)}: {snippet}
          {targetLabel && d.action === 'delegate' && (
            <span className="ml-1 text-slate-500 dark:text-slate-400">
              → {t('chat:orchestrator.delegatingTo', 'delegating to {{agent}}', { agent: targetLabel })}
            </span>
          )}
        </SurfaceSystemMessage>
      )
    }

    case 'delegation': {
      const d = msg.data as {
        agentLabel?: string
        task?: string
        fromAgent?: string
        toAgent?: string
        reason?: string
      }
      if (d.agentLabel && d.task) {
        return <ToolActivityRow agentName={d.agentLabel} status="started" activity={d.task} />
      }
      return (
        <SurfaceSystemMessage variant="info" isLast>
          {d.fromAgent && d.toAgent
            ? t('chat:delegation.handoff', '{{from}} delegated to {{to}}', { from: d.fromAgent, to: d.toAgent })
            : t('chat:delegation.generic', 'Task delegated to another agent')}
          {d.reason && <span className="ml-1 text-slate-400">— {d.reason}</span>}
        </SurfaceSystemMessage>
      )
    }

    case 'delegation_result': {
      const d = msg.data as {
        agentLabel?: string
        success?: boolean
        summary?: string
        error?: string
        durationMs?: number
        outcome?: string
        blockedReason?: string
      }
      const durationSec = d.durationMs ? d.durationMs / 1000 : undefined
      // The mission setup refused the agent's request (agion/Agion#2370): the
      // fixed phrase for its refusal code, never the runner's error text.
      if (resolveDelegationState(d.success, d.outcome) === 'blocked') {
        const agentName = d.agentLabel || t('common:agent', 'Agent')
        return (
          <ToolActivityRow
            agentName={agentName}
            status="blocked"
            blockedLabel={blockedLabels.blocked}
            result={blockedReasonText(d.blockedReason, agentName, blockedLabels)}
            duration={durationSec}
          />
        )
      }
      return (
        <ToolActivityRow
          agentName={d.agentLabel || t('common:agent', 'Agent')}
          status={d.success ? 'completed' : 'failed'}
          result={d.success ? d.summary : d.error}
          duration={durationSec}
        />
      )
    }

    case 'agent_thinking': {
      const d = msg.data as {
        content?: string
        redacted?: boolean
        model?: string
      }
      return (
        <ThinkingRow
          agentName={resolveAgentName(msg.agentId, msg.nodeId)}
          currentStep={d.redacted
            ? t('chat:thinking.redacted', 'Thinking (details redacted)...')
            : t('chat:thinking.active', 'Thinking...')}
        />
      )
    }

    case 'system_notification': {
      const d = msg.data as {
        message?: string
        severity?: 'info' | 'warning' | 'success' | 'error'
      }
      return (
        <SurfaceSystemMessage variant={d.severity || 'info'} isLast>
          {d.message || t('chat:system.notification', 'System notification')}
        </SurfaceSystemMessage>
      )
    }

    case 'transcript': {
      const d = msg.data as {
        speaker: 'user' | 'agent'
        text: string
        isFinal: boolean
        confidence?: number
        language?: string
      }
      if (!d.text) return null
      if (d.speaker === 'user') {
        // A transcript names no speaker, so it is not attributed to the viewer (agion/Agion#2039).
        return (
          <SurfaceUserMessage
            userName={resolveUserName()}
            isCurrentUser={false}
            isLast
            showHeader={!hideUserHeader}
          >
            <p className={`whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${!d.isFinal ? 'italic text-slate-400 dark:text-slate-500' : ''}`}>
              {d.text}
            </p>
          </SurfaceUserMessage>
        )
      }
      return (
        <SurfaceAgentResponse
          agentName={resolveAgentName(msg.agentId, msg.nodeId)}
          isLast
          showHeader={!hideAgentHeader}
        >
          <p className={`whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${!d.isFinal ? 'italic text-slate-400 dark:text-slate-500' : ''}`}>
            {d.text}
          </p>
        </SurfaceAgentResponse>
      )
    }

    case 'mission_completed':
    case 'mission_failed':
      return null

    // Activity sidebar only (placement module).
    case 'node_status':
      return null

    // A person's reply in an operator takeover (agion/Agion#3435): the
    // person's own surface and name, marked as a person, never the agent's
    // name or avatar.
    case 'operator_turn': {
      const d = msg.data as { operatorUserId?: string; operatorDisplayName?: string; text?: string }
      if (!d.text) return null
      return (
        <div data-testid="operator-turn">
          <SurfaceUserMessage
            userName={d.operatorDisplayName || t('common:unnamedPerson', 'Name not recorded')}
            isCurrentUser={!!currentUserId && d.operatorUserId === currentUserId}
            isLast
          >
            <span
              className="mb-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-700 dark:text-slate-300"
              data-testid="operator-person-marker"
            >
              {t('chat:operatorTakeover.personMarker', 'Person')}
            </span>
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{d.text}</p>
          </SurfaceUserMessage>
        </div>
      )
    }

    default:
      // A kind this build does not know never reaches the conversation: the
      // placement module puts it in the Activity sidebar.
      return unknownKind(msg.type, null)
  }
}
